<template>
  <PVStep :value="value" as-child>
    <div class="step-container">
      <div class="step">
        <div
          :class="{
            'step-circle': true,
            'step-circle--current': isCurrentStep,
            'step-circle--current--with-dropdown': isDropdown,
            'step-circle--completed': isStepCompleted,
            'step-circle--failed': isStepFailed,
          }"
        >
          <font-awesome-icon v-if="isStepCompleted" :icon="['fas', 'check']" />
          <font-awesome-icon
            v-else-if="isStepFailed"
            :icon="['fas', 'xmark']"
          />
          <div v-else class="step-display-value">
            {{ displayValue ?? value }}
          </div>
        </div>
      </div>
      <PVDivider
        v-if="!isLastStep"
        :pt="{
          root: {
            style: {
              'border-block-start': '5px solid black',
              'border-block-start-color': isStepCompleted
                ? 'var(--p-primary-color)'
                : 'black',
            },
          },
        }"
      />
      <VDropdown v-if="isDropdown" :auto-hide="false">
        <div
          :class="{
            'step-label': true,
            'step-label--current': isCurrentStep,
            'step-label--current--with-dropdown': isCurrentStep && isDropdown,
          }"
        >
          {{ label }}
        </div>
        <template #popper>
          <slot name="dropdown-content"> </slot>
        </template>
      </VDropdown>
      <div v-else class="step-label">{{ label }}</div>
    </div>
  </PVStep>
</template>

<script lang="ts" setup>
  import { computed } from 'vue';

  const props = defineProps<{
    currentStepperValue: number;
    displayValue?: string | number;
    isCompleted?: boolean;
    isFailed?: boolean;
    label: string;
    steps: number[];
    value: number;
  }>();

  const slots = defineSlots<{
    'dropdown-content'?: () => void;
  }>();

  const isDropdown = computed(() => {
    return isCurrentStep.value && !!slots?.['dropdown-content'];
  });
  const isLastStep = computed(() => {
    return [...props.steps].pop() === props.value;
  });
  const isCurrentStep = computed(() => {
    return props.currentStepperValue === props.value;
  });
  const isStepFailed = computed(() => {
    return props.isFailed && isCurrentStep.value;
  });
  const isStepCompleted = computed(() => {
    return (
      props.steps.indexOf(props.value) <
        props.steps.indexOf(props.currentStepperValue) ||
      (isCurrentStep.value && props.isCompleted)
    );
  });
</script>

<style lang="scss" scoped>
  .step-container {
    width: 100%;
    display: grid;
    grid-template-columns: 2.5rem 1fr;
    grid-template-rows: auto auto;
    align-items: start;
    &:last-of-type {
      grid-template-columns: 2.5rem;
      width: auto;
    }
    .step {
      display: flex;
      flex-direction: column;
      align-items: center;

      .step-circle {
        width: 2.5rem;
        height: 2.5rem;
        border: 5px solid black;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: transparent;
        color: black;
        &--current {
          border-color: var(--p-primary-color);
          &--with-dropdown {
            border-color: $dynamic-orange;
          }
        }
        &--completed,
        &--failed {
          border-color: var(--p-primary-color);
          background-color: var(--p-primary-color);
          color: white;
        }
        .step-display-value {
          font-weight: bold;
          font-size: 0.9rem;
          user-select: none;
        }
      }
    }
    .v-popper {
      justify-self: center;
    }
    .step-label {
      text-wrap: nowrap;
      justify-self: center;
      margin-top: 0.3rem;
      user-select: none;
      font-weight: bold;
      &--current {
        &--with-dropdown {
          cursor: pointer;
          color: $dynamic-orange;
          font-weight: bold;
        }
      }
    }
  }
</style>
